import { registerLocaleData } from "@angular/common";
import {
    HTTP_INTERCEPTORS,
    provideHttpClient,
    withInterceptorsFromDi,
    withJsonpSupport,
} from "@angular/common/http";
import localeEn from "@angular/common/locales/en";
import localeEs419 from "@angular/common/locales/es-419";
import localeFr from "@angular/common/locales/fr";
import localePt from "@angular/common/locales/pt";
import {
    ApplicationConfig,
    importProvidersFrom,
    inject,
    Injector,
    provideAppInitializer,
    provideZoneChangeDetection,
} from "@angular/core";
import { provideAnimations } from "@angular/platform-browser/animations";
import { provideRouter, withInMemoryScrolling } from "@angular/router";

import { getAnalytics, provideAnalytics } from "@angular/fire/analytics";
import { getApp, initializeApp, provideFirebaseApp } from "@angular/fire/app";
import {
    initializeAppCheck,
    provideAppCheck,
    ReCaptchaV3Provider,
} from "@angular/fire/app-check";
import { getAuth, provideAuth } from "@angular/fire/auth";
import { getDatabase, provideDatabase } from "@angular/fire/database";
import {
    initializeFirestore,
    memoryEagerGarbageCollector,
    memoryLocalCache,
    provideFirestore,
} from "@angular/fire/firestore";
import { getMessaging, provideMessaging } from "@angular/fire/messaging";
import { getPerformance, providePerformance } from "@angular/fire/performance";
import { getStorage, provideStorage } from "@angular/fire/storage";

import { GoogleMapsModule } from "@angular/google-maps";
import { provideNativeDateAdapter } from "@angular/material/core";
import {
    MAT_DIALOG_DEFAULT_OPTIONS,
    MatDialogModule,
    MatDialogRef,
} from "@angular/material/dialog";
import { MAT_FORM_FIELD_DEFAULT_OPTIONS } from "@angular/material/form-field";
import { MatIconModule } from "@angular/material/icon";
import { MatPaginatorIntl } from "@angular/material/paginator";
import { MAT_SELECT_CONFIG } from "@angular/material/select";
import { MatSidenavModule } from "@angular/material/sidenav";
import { MatSnackBarModule } from "@angular/material/snack-bar";

import { Ability, PureAbility } from "@casl/ability";
import { provideTransloco } from "@jsverse/transloco";
import mixpanel from "mixpanel-browser";
import { provideMarkdown } from "ngx-markdown";
import { provideNgxSkeletonLoader } from "ngx-skeleton-loader";

import { environment } from "../environments/environment";
import { AppConfigService } from "./app.config.service";
import { APP_ROUTES } from "./app.routes";
import { HttpErrorTranslatorInterceptor } from "./interceptors/http-error-translator.interceptor";
import { InnerSidenavHandlerService } from "./main/core/services/inner-sidenav-handler.service";
import { MIXPANEL_TOKEN } from "./shared/services/analytics/analytics.constants";
import { AppCheckInterceptor } from "./shared/services/firebase/app-check.interceptor";
import { MatPaginatorIntlCustomService } from "./shared/services/mat-paginator-intl-custom.service";
import { VITALLY_TOKEN } from "./shared/services/nps/nps.constants";
import { Vitally } from "./shared/services/nps/vitally/vitally.interfaces";
import { TranslocoHttpLoader } from "./transloco-http-loader";
import {
    appConfigEnvironmentFactory,
    appConfigFactory,
    serviceWorkerInitializerFactory,
} from "./utils/functions/app-config.factory";

// Register locales
registerLocaleData(localeEs419, "es-419");
registerLocaleData(localePt, "pt");
registerLocaleData(localeFr, "fr");
registerLocaleData(localeEn, "en");

if (!environment.production) {
    (self as any).FIREBASE_APPCHECK_DEBUG_TOKEN = environment.dt;
}

const TIMEOUT_FLAG = JSON.parse(localStorage.getItem("timeoutOn") ?? "false");


export const appConfig: ApplicationConfig = {
    providers: [
        provideZoneChangeDetection({ eventCoalescing: true }),
        provideRouter(
            APP_ROUTES,
            withInMemoryScrolling({ scrollPositionRestoration: "enabled" }),
        ),
        provideAnimations(),
        provideNativeDateAdapter(),
        provideMarkdown(),
        provideNgxSkeletonLoader(),
        importProvidersFrom(
            MatIconModule,
            MatDialogModule,
            MatSnackBarModule,
            MatSidenavModule,
            GoogleMapsModule,
        ),
        provideTransloco({
            config: {
                availableLangs: ["en", "es", "pt", "fr"],
                defaultLang: "es",
                fallbackLang: "es",
                reRenderOnLangChange: true,
                prodMode: environment.production,
                flatten: {
                    aot: environment.production,
                },
            },
            loader: TranslocoHttpLoader,
        }),
        provideFirebaseApp(() => initializeApp(environment.firebase)),
        provideAuth(() => getAuth(getApp())),
        providePerformance(() => getPerformance()),
        provideAnalytics(() => getAnalytics()),
        provideFirestore(() =>
            initializeFirestore(getApp(), {
                experimentalAutoDetectLongPolling: TIMEOUT_FLAG,
                localCache: memoryLocalCache({
                    garbageCollector: memoryEagerGarbageCollector(),
                }),
            }),
        ),
        provideMessaging(() => getMessaging()),
        provideStorage(() => getStorage()),
        provideAppCheck(() =>
            initializeAppCheck(getApp(), {
                provider: new ReCaptchaV3Provider(
                    environment.firebase.recaptchaSiteKey,
                ),
                isTokenAutoRefreshEnabled: true,
            }),
        ),
        provideDatabase(() => getDatabase()),
        provideHttpClient(withInterceptorsFromDi(), withJsonpSupport()),
        { provide: MatDialogRef, useValue: {} },
        { provide: Ability, useValue: new Ability() },
        { provide: PureAbility, useExisting: Ability },
        provideAppInitializer(() => {
            const initializerFn = appConfigFactory(inject(AppConfigService));
            return initializerFn();
        }),
        provideAppInitializer(() => {
            const initializerFn = serviceWorkerInitializerFactory(
                inject(AppConfigService),
            );
            return initializerFn();
        }),
        {
            provide: "environment",
            useFactory: appConfigEnvironmentFactory,
            deps: [AppConfigService],
        },
        {
            provide: MIXPANEL_TOKEN,
            useValue: mixpanel,
        },
        {
            provide: VITALLY_TOKEN,
            useValue: (
                window as unknown as {
                    Vitally?: Vitally;
                }
            ).Vitally,
        },
        {
            provide: MatPaginatorIntl,
            useClass: MatPaginatorIntlCustomService,
        },
        {
            provide: HTTP_INTERCEPTORS,
            useClass: AppCheckInterceptor,
            multi: true,
        },
        {
            provide: HTTP_INTERCEPTORS,
            useClass: HttpErrorTranslatorInterceptor,
            multi: true,
        },
        {
            provide: MAT_DIALOG_DEFAULT_OPTIONS,
            useValue: {
                restoreFocus: false,
            },
        },
        {
            provide: MAT_FORM_FIELD_DEFAULT_OPTIONS,
            useValue: {
                appearance: "outline",
            },
        },
        {
            provide: MAT_SELECT_CONFIG,
            useValue: {
                hideSingleSelectionIndicator: true,
            },
        },
        InnerSidenavHandlerService,
        { provide: "injector", useExisting: Injector },
    ],
};
